import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { create } from 'zustand';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { authApi, TOKEN_KEY } from '@api/client';
import type { Usuario, AuthState } from '@/types';

import { extractApiError } from '@utils/apiError';
import { lecturasPermitidas } from '@utils/accesoModulos';
import type { LecturasPermitidas } from '@erp/shared';
// ─── Zustand Store ────────────────────────────────────────────
interface AuthStore extends AuthState {
  setUsuario: (usuario: Usuario | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

const useAuthStore = create<AuthStore>((set) => ({
  usuario: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,

  setUsuario: (usuario) =>
    set({ usuario, isAuthenticated: !!usuario }),

  setToken: (token) =>
    set({ token }),

  setLoading: (isLoading) =>
    set({ isLoading }),

  reset: () =>
    set({
      usuario: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    }),
}));

// ─── Context ──────────────────────────────────────────────────
interface AuthContextValue {
  usuario: Usuario | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { usuario, token, isLoading, isAuthenticated, setUsuario, setToken, setLoading, reset } =
    useAuthStore();

  // Usar ref para navigate porque el provider se monta antes del router
  const navigateRef = useRef<((path: string) => void) | null>(null);
  const queryClient = useQueryClient();

  function useNavigateSafe() {
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useNavigate();
    } catch {
      return null;
    }
  }

  const navigate = useNavigateSafe();

  useEffect(() => {
    if (navigate) {
      navigateRef.current = navigate;
    }
  }, [navigate]);

  /**
   * Verifica si hay un token guardado y carga los datos del usuario desde la BD.
   * En modo DEV local con VITE_DEV_MOCK_USER=true se inyecta un CEO mock sin backend.
   * NUNCA funciona en producción (import.meta.env.DEV es false en el build de Vite).
   */
  const checkAuth = useCallback(async (): Promise<void> => {
    // ── DEV-ONLY BYPASS — eliminado automáticamente en build de producción ──
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER === 'true') {
      console.warn('[AuthContext] DEV MOCK activo — nunca usar en producción');
      setUsuario({
        id: 1,
        nombre: 'Roberto Méndez (Demo)',
        email: 'ceo@rm-desarrollos.com',
        rol: 'ceo',
        nivel: 4,
        activo: true,
        proyectos: [1, 2, 3],
        modulos: [],
        accesos: { ingresos: true, egresos: true, bancos: true, creditos: true, contabilidad: true, corporativas: true, cuentasEmpresa: true }, // mock CEO (sólo DEV)
        lecturas: { resumenFinanciero: true, presupuesto: true, cobranza: true, cuentas: true, creditos: true, alertas: true, ritmoVenta: true, catalogosDeEgresos: true, recurrentesDeEgresos: true }, // mock CEO (sólo DEV)
      });
      setToken('dev-mock-token');
      setLoading(false);
      return;
    }
    // ── FIN DEV BYPASS ──────────────────────────────────────────

    const savedToken = localStorage.getItem(TOKEN_KEY);

    if (!savedToken) {
      reset();
      return;
    }

    setLoading(true);
    setToken(savedToken);

    try {
      const response = await authApi.me();
      setUsuario(response.data.data);
    } catch {
      // Token inválido o expirado
      localStorage.removeItem(TOKEN_KEY);
      reset();
    } finally {
      setLoading(false);
    }
  }, [reset, setLoading, setToken, setUsuario]);

  /**
   * Inicia sesión con email y password.
   * Guarda el access_token en localStorage.
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setLoading(true);
      try {
        const response = await authApi.login(email, password);
        const { access_token, usuario: usuarioBasico } = response.data.data;

        queryClient.clear(); // I-230: un login nuevo no hereda el caché de otro rol
        localStorage.setItem(TOKEN_KEY, access_token);
        setToken(access_token);

        // El endpoint login devuelve datos básicos (sin proyectos/modulos).
        // Llamamos /auth/me para obtener el usuario completo con todos los campos.
        const meResponse = await authApi.me();
        const usuarioData = meResponse.data.data;
        setUsuario(usuarioData);

        toast.success(`Bienvenido, ${usuarioBasico.nombre.split(' ')[0]}`);
        navigateRef.current?.('/dashboard');
      } catch (error: unknown) {
        // BUG-137: antes CUALQUIER fallo caía en "Credenciales incorrectas" — incluido
        // que el servidor estuviera apagado o que la CSP/CORS bloqueara la llamada. El
        // 2026-08-10 eso mandó a buscar una contraseña mala durante un buen rato cuando
        // el problema era de red. Un error de conexión NO es una contraseña equivocada.
        const err = error as {
          code?: string;
          response?: { status?: number };
        };

        // Sin `response` = la petición nunca llegó (servidor caído, CORS, CSP, sin internet).
        if (!err?.response) {
          throw new Error(
            err?.code === 'ECONNABORTED'
              ? 'El servidor tardó demasiado en responder. Revisa tu conexión e inténtalo de nuevo.'
              : 'No se pudo contactar al servidor. Revisa tu conexión o avisa a soporte — no es tu contraseña.',
          );
        }

        const status = err.response.status ?? 0;
        if (status >= 500) {
          throw new Error('El servidor tuvo un problema al procesar tu acceso. Avisa a soporte — no es tu contraseña.');
        }

        throw new Error(
          extractApiError(err, 'Credenciales incorrectas. Verifica tu email y contraseña.'),
        );
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setToken, setUsuario, queryClient],
  );

  /**
   * Cierra sesión: limpia localStorage y redirige a /login.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Ignorar errores en logout — siempre limpiar localmente
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      // I-230 (review adversarial): el caché de consultas NO es por usuario. Sin esto, quien entra después en la misma
      // pestaña ve (hasta 5 min) el tablero/cuentas del anterior — con los saldos que su rol ya no ve — o al revés.
      queryClient.clear();
      reset();
      navigateRef.current?.('/login');
    }
  }, [reset, queryClient]);

  /**
   * Refresca el access_token usando la httpOnly cookie del refresh token.
   */
  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const response = await authApi.refresh();
      const { access_token } = response.data.data;
      localStorage.setItem(TOKEN_KEY, access_token);
      setToken(access_token);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      reset();
    }
  }, [reset, setToken]);

  // Verificar auth al montar
  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calcula cuántos ms faltan para refrescar: 2 min antes del exp real del JWT.
  // Evita el bug donde el timer fijo de 13 min dispara tarde si el token ya tenía vida acumulada.
  function msUntilRefresh(jwt: string): number {
    try {
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return Math.max(0, payload.exp * 1000 - Date.now() - 2 * 60 * 1000);
    } catch {
      return (15 - 2) * 60 * 1000;
    }
  }

  // Refresh proactivo: timer basado en el exp real del JWT, no en un fixed 13 min.
  useEffect(() => {
    if (!token) return;
    const delay = msUntilRefresh(token);
    const timer = setTimeout(() => refreshToken(), delay);
    return () => clearTimeout(timer);
  }, [token, refreshToken]);

  // Cuando el tab vuelve a ser visible, refrescar si quedan < 60 s de vida.
  // Cubre el caso donde el browser suspendió el timer mientras el tab estaba en background.
  useEffect(() => {
    if (!token) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && msUntilRefresh(token) < 60_000) {
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token, refreshToken]);

  const value: AuthContextValue = {
    usuario,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return ctx;
}

/**
 * BUG-4118 — qué lecturas puede PEDIR la pantalla (las calcula el servidor, `/auth/me` → `lecturas`). Sin sesión no se pide ninguna.
 * No truena como `useAuth` si no hay <AuthProvider>: en la app todo va dentro de él (`main.tsx`); sólo las pruebas montan las fichas sueltas, sin sesión (⇒ ninguna lectura).
 */
export function useLecturasPermitidas(): LecturasPermitidas {
  return lecturasPermitidas(useContext(AuthContext)?.usuario);
}
